import {
  FormCheckbox,
  FormSelect,
  FormTextField,
} from '@skupreme/shared/ui/form';
import { ERROR_MESSAGES } from '@utility/validations';
import type { SelectOption } from '@skupreme/shared/ui';
import type { StepProps } from './types';
import { KindLabel } from '../KindLabel';

type LocationStepProps = StepProps & {
  countryOptions: SelectOption[];
  isZipCodeValid: boolean;
  /** Set once a country and a ZIP are both filled in. */
  hasZipAndCountry: boolean;
  /** Extensiv warehouses are both kinds, so the choice is not offered. */
  isExtensiv: boolean;
  /** Both boxes empty although every field is valid. */
  needsProviderKind: boolean;
};

export function LocationStep({
  control,
  fieldError,
  isVisible,
  countryOptions,
  isZipCodeValid,
  hasZipAndCountry,
  isExtensiv,
  needsProviderKind,
}: LocationStepProps) {
  const zipError =
    !isZipCodeValid && hasZipAndCountry
      ? ERROR_MESSAGES.INVALID_ZIP_CODE
      : fieldError('zip_code');

  return (
    <div className={isVisible ? 'sk:flex sk:flex-col sk:gap-4' : 'sk:hidden'}>
      <FormTextField
        control={control}
        name="email"
        label="Storage Provider E-Mail Address"
        placeholder="E-Mail Address"
        error={fieldError('email')}
        isRequired={isVisible}
      />
      <div className="sk:grid sk:grid-cols-2 sk:gap-4">
        <FormTextField
          control={control}
          name="address_line_one"
          label="Address 1"
          error={fieldError('address_line_one')}
          isRequired={isVisible}
        />
        <FormTextField
          control={control}
          name="address_line_two"
          label="Address 2"
          error={fieldError('address_line_two')}
        />
        <FormTextField
          control={control}
          name="city"
          label="City"
          error={fieldError('city')}
          isRequired={isVisible}
        />
        <FormTextField
          control={control}
          name="state"
          label="State"
          error={fieldError('state')}
          isRequired={isVisible}
        />
        <FormTextField
          control={control}
          name="zip_code"
          label="ZIP"
          error={zipError}
          isRequired={isVisible}
        />
        <FormSelect
          control={control}
          name="country"
          label="Country"
          placeholder="Select a country"
          options={countryOptions}
          error={fieldError('country')}
          isRequired={isVisible}
        />
      </div>
      {isExtensiv ? null : (
        <div className="sk:flex sk:flex-col sk:gap-2">
          <span className="sk:text-dense sk:font-medium sk:text-fg-secondary">
            Storage Provider Type
          </span>
          <div className="sk:grid sk:grid-cols-2 sk:gap-4">
            <FormCheckbox
              control={control}
              name="is_fulfillable"
              label={
                <KindLabel
                  title="Fulfills Individual Orders"
                  detail="(Stores and Ships Individual Items)"
                />
              }
            />
            <FormCheckbox
              control={control}
              name="is_warehouse"
              label={
                <KindLabel
                  title="Offers Storage and Forwarding"
                  detail="(Stores Parcels / Pallets)"
                />
              }
            />
          </div>
          {needsProviderKind ? (
            <span className="sk:text-xs sk:text-danger">
              Please select at least one checkbox
            </span>
          ) : null}
        </div>
      )}
    </div>
  );
}
